'use strict';

define('vb/private/stateManagement/switcherRootRouter',[
  'vb/private/stateManagement/router/vbRootRouter',
  'vb/private/stateManagement/router/historyAdapter',
  'vb/private/stateManagement/switcherHistory',
  'vb/private/stateManagement/router',
  'vb/private/constants',
], (VbRootRouter, HistoryAdapter, SwitcherHistory, Router, Constants) => {
  /**
   * @typedef {import('vb/private/stateManagement/page').default} Page
   */

  /**
   * A place to store all instances of SwitcherRootRouter in the application
   * @type {Object}
   */
  const allRootRouters = {};

  class SwitcherRootRouter extends VbRootRouter {
    /**
     * @param {Object} switcher
     * @param {Object} switcherElement
     * @param {string} fullPath
     */
    constructor(switcher, switcherElement, fullPath) {
      const key = `switcher-${switcherElement.id}`;
      super(key, new HistoryAdapter(`${fullPath}/`));

      if (allRootRouters[key]) {
        throw new Error(`Root router ${key} already exist.`);
      }

      allRootRouters[key] = this;

      this.switcher = switcher;
      this.switcherElement = switcherElement;
      // Replace the history implementation
      this.history = SwitcherHistory;

      /**
       * The most recent nested-router transition started for this switcher element.
       * @type {Promise<{hasChanged: boolean}>}
       * @private
       */
      this._navigationPromise = Promise.resolve({ hasChanged: false });
    }

    /**
     * Start a nested-router transition and retain its promise for the switcher display lifecycle.
     *
     * Container.navigateToOtherPage() intentionally does not wait for router.go(). The switcher needs
     * that promise to defer its displayed-content notification until the new nested router has created
     * its page module configuration.
     *
     * @param {string} stateIdPath the path to navigate to
     * @param {Object} [options] router transition options
     * @return {Promise<{hasChanged: boolean}>}
     */
    go(stateIdPath, options) {
      const navigationPromise = super.go(stateIdPath, options);
      this._navigationPromise = navigationPromise;

      // Keep the rejection visible to callers waiting on this transition, but do not retain it as
      // the readiness state for every later tab selection. Do not clear a newer navigation that
      // may have started before this one rejected.
      navigationPromise.catch(() => {
        if (this._navigationPromise === navigationPromise) {
          this._navigationPromise = Promise.resolve({ hasChanged: false });
        }
      });

      return navigationPromise;
    }

    /**
     * Get the most recent nested-router transition promise.
     *
     * @return {Promise<{hasChanged: boolean}>}
     */
    getNavigationPromise() {
      return this._navigationPromise;
    }

    /**
     * Retrieve the VbRootRouter map
     * @param {string} name
     * @returns {VbRootRouter}
     */
    static getRootInstanceFromName(name) {
      return allRootRouters[name];
    }

    /**
     * Override VbRootRouter.setTopRootInstance
     *
     * When the root router for a switcher is created, it should not replace the top
     * root router instance.
     */
    // eslint-disable-next-line class-methods-use-this
    setTopRootInstance() {
    }

    /**
     * Called as a result of a history popstate event
     *
     * @param {{direction: {string}, change: {number}}} details
     */
    handlePopState(details) {
      /**
       * It is important to add a popstate event listener directly because it will be
       * handle after the ojRouter popstate. If we were using popStateCallback in Router
       * it would be executed first and the navigation will be cancelled by ojRouter.
       */
      if (!this.switcher.handlePopState(details)) {
        super.handlePopState(details);
      }
    }

    /**
     * Switcher does not update URL, it only updates the browser state
     * @param {Array<Object>} newStates'
     * @param {boolean} replace
     */
    updateUrl(newStates, replace) {
      this.updateState(null, replace);
    }

    /**
     * Update the browser history with the new state of the switcher
     * Save the page path in the browser history
     * @param  {String|null} pagePath - The new page path to set in the browser's history.
     * @param  {Boolean} [replace=false] - A flag to indicate the state should be pushed or replaced
     * @return {Promise<void>} a promise that resolve when the browser state is updated
     */
    updateState(pagePath, replace) {
      return Promise.resolve().then(() => {
        this.switcherElement.storeInHistory();

        return super.updateState(pagePath, replace);
      });
    }

    /**
     * Implementation of the navigate back action is different for the switcher.
     * Instead of doing browser back, it navigates to the previous page in a switcher
     * element.
     * @param {Page} page
     * @param {Object} [params]
     * @returns {Promise<void>}
     */
    navigateBack(page, params) {
      return Promise.resolve().then(() => {
        // In order to find the previous path in the history stack entry, it needs to use the leaf page full path
        // because the full path is how history entries are recorded.
        const result = this.history.getPathBeforePreviousVisit(page.getLeafContainer().fullPath);
        // If the entry is not found, execute the default behavior
        if (result === null) {
          return super.navigateBack(page, params);
        }

        if (result === '') {
          throw new Error('There is no page to navigate back.');
        }

        let destinationPath = this.convertFullPathToLocalSwitcherPath(result);
        if (!destinationPath) {
          throw new Error('Invalid path information in browser history.');
        }

        // Check if the first segment of the path is an AppUi. In  that case it needs tp
        // be an appUi navigation.
        const firstSlashIndex = destinationPath.indexOf('/');
        const firstSegment = destinationPath.substring(0, firstSlashIndex);
        let application;
        let operation;

        if (page.application.doesAppUiExist(firstSegment)) {
          // For a browser history path looking like seg1/seg2/seg3, if seg1 is an AppUi, then
          // the navigation should be /seg2/seg3 where seg2 is either a root page or a default flow
          // depending on the AppUI.
          operation = Constants.NavigateOperation.PAGE;
          application = firstSegment;
          // The path start with a '/' because the navigation should start at the App UI.
          // Without the '/' the navigation would start at the default page or flow for the AppUi.
          destinationPath = destinationPath.substring(firstSlashIndex);
        } else {
          operation = Constants.NavigateOperation.PAGE_OLD;
        }
        // Navigate forward to the page that is the previous one in the stack
        return Router.queueNavigate(this.switcherElement.root, {
          application,
          page: destinationPath,
          operation,
          params,
        }).then(() => undefined);
      });
    }

    /**
     * Extract the part of the page path after the switcher root page
     * @param {string} path
     * @returns {string}
     */
    convertFullPathToLocalSwitcherPath(path) {
      const switcherIndex = path.indexOf(this.name);
      if (switcherIndex === -1) {
        return '';
      }

      const startIndex = switcherIndex + this.name.length + 1;
      return path.substring(startIndex);
    }

    dispose() {
      // Remove itself from the cached rootRouters
      delete allRootRouters[this.name];
      super.dispose();
    }
  }

  return SwitcherRootRouter;
});

